import { Injectable, inject } from "@angular/core";
import { GoogleMapsService } from "./google-maps.service";

export interface PlaceSuggestion {
  label: string;
  prediction: google.maps.places.PlacePrediction;
}

export interface PlaceDetails {
  label: string;
  latitude: number;
  longitude: number;
  postalCode: string;
  country: string;
}

@Injectable({ providedIn: "root" })
export class PlaceAutocompleteService {
  private googleMapsService = inject(GoogleMapsService);

  // A session is every keystroke up to the details call that closes it, and it
  // is what the Places API bills as one autocomplete instead of per request.
  private sessionToken: google.maps.places.AutocompleteSessionToken;

  // Pulls the library in before the first keystroke needs it — otherwise the
  // first search waits on the API bootstrap on top of its own round trip.
  public preload(): void {
    void this.googleMapsService.loadLibrary("places").catch(() => undefined);
  }

  public async fetchSuggestions(input: string): Promise<PlaceSuggestion[]> {
    const { AutocompleteSuggestion, AutocompleteSessionToken } =
      await this.googleMapsService.loadLibrary("places");

    if (!this.sessionToken) {
      this.sessionToken = new AutocompleteSessionToken();
    }

    const { suggestions } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: this.sessionToken,
      });

    // Query predictions carry no place, so there is nothing to geocode.
    return suggestions
      .filter((suggestion) => !!suggestion.placePrediction)
      .map((suggestion) => ({
        label: suggestion.placePrediction.text.text,
        prediction: suggestion.placePrediction,
      }));
  }

  public async fetchDetails(
    suggestion: PlaceSuggestion,
  ): Promise<PlaceDetails> {
    const place = suggestion.prediction.toPlace();
    await place.fetchFields({
      // `postalAddress` sits in the same billing tier as the other two and
      // carries a postal code for places whose components have none.
      fields: ["location", "addressComponents", "postalAddress"],
    });

    // The details call ends the session — the next keystroke opens a new one.
    this.sessionToken = undefined;

    return {
      label: suggestion.label,
      latitude: place.location?.lat(),
      longitude: place.location?.lng(),
      postalCode:
        this.findComponent(place, "postal_code") ??
        place.postalAddress?.postalCode ??
        null,
      country:
        this.findComponent(place, "country") ??
        place.postalAddress?.regionCode ??
        null,
    };
  }

  private findComponent(
    place: google.maps.places.Place,
    type: string,
  ): string | null {
    const component = place.addressComponents?.find((addressComponent) =>
      addressComponent.types.includes(type),
    );

    return component ? component.shortText : null;
  }
}
